import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '../../services/toast.service';
import { ModuloDashboard, WidgetCatalogo } from '../../types/Dashboard.type';
import { DashboardApiService } from '../dashboard-api.service';
import { ItemLocal, Posicao, cabe, ocupacao, primeiraPosicaoLivre } from '../dashboard-layout';

export interface WidgetEscolhido {
  widget: WidgetCatalogo;
  posicao: Posicao;
}

/** Modal "Adicionar widget": módulos → cards (FRONTEND.md §3.4). */
@Component({
  selector: 'app-adicionar-widget',
  templateUrl: './adicionar-widget.component.html',
  styleUrl: './adicionar-widget.component.css'
})
export class AdicionarWidgetComponent {

  @Input() visivel = false;
  @Output() visivelChange = new EventEmitter<boolean>();

  /** Itens atuais do dashboard, para validar se o widget cabe. */
  @Input() itens: ItemLocal[] = [];
  /** Célula escolhida pelo + do slot; null = primeira posição livre. */
  @Input() slot: Posicao | null = null;

  @Output() escolher = new EventEmitter<WidgetEscolhido>();

  modulos: ModuloDashboard[] = [];
  moduloSelecionado: ModuloDashboard | null = null;
  widgets: WidgetCatalogo[] = [];
  carregandoModulos = false;
  carregandoWidgets = false;
  mensagemNaoCabe: string | null = null;

  readonly miniatura = Array.from({ length: 8 }, (_, i) => ({ linha: Math.floor(i / 4), coluna: i % 4 }));

  constructor(private dashboardApi: DashboardApiService, private toastService: ToastService) { }

  aoAbrir() {
    this.modulos = [];
    this.widgets = [];
    this.moduloSelecionado = null;
    this.mensagemNaoCabe = null;
    this.carregandoModulos = true;

    this.dashboardApi.modulos().subscribe({
      next: (modulos) => {
        this.carregandoModulos = false;
        this.modulos = [...modulos].sort((a, b) => (a.descricao || a.nome).localeCompare(b.descricao || b.nome));
        if (this.modulos.length) this.selecionarModulo(this.modulos[0]);
      },
      error: (error: HttpErrorResponse) => {
        this.carregandoModulos = false;
        this.toastService.erro('Erro', this.mensagem(error, 'Erro ao buscar os módulos'));
      }
    });
  }

  fechar() {
    this.visivel = false;
    this.visivelChange.emit(false);
  }

  selecionarModulo(modulo: ModuloDashboard) {
    this.moduloSelecionado = modulo;
    this.mensagemNaoCabe = null;
    this.widgets = [];
    this.carregandoWidgets = true;

    this.dashboardApi.widgetsDoModulo(modulo.id).subscribe({
      next: (widgets) => {
        if (this.moduloSelecionado !== modulo) return;
        this.carregandoWidgets = false;
        this.widgets = widgets;
      },
      error: (error: HttpErrorResponse) => {
        this.carregandoWidgets = false;
        this.toastService.erro('Erro', this.mensagem(error, 'Erro ao buscar os widgets do módulo'));
      }
    });
  }

  /** Só se aplica quando o modal foi aberto por um slot. */
  naoCabe(widget: WidgetCatalogo): boolean {
    return !!this.slot && !cabe(this.slot.linha, this.slot.coluna, widget.largura, widget.altura, ocupacao(this.itens));
  }

  selecionarWidget(widget: WidgetCatalogo) {
    const posicao = this.slot ?? primeiraPosicaoLivre(this.itens, widget.largura, widget.altura);

    if (!cabe(posicao.linha, posicao.coluna, widget.largura, widget.altura, ocupacao(this.itens))) {
      this.mensagemNaoCabe = `O widget ${widget.nome} ocupa ${widget.largura} × ${widget.altura} e não cabe nesta posição.`;
      return;
    }

    this.escolher.emit({ widget, posicao });
    this.fechar();
  }

  ocupadaNaMiniatura(widget: WidgetCatalogo, celula: Posicao): boolean {
    return celula.coluna < widget.largura && celula.linha < widget.altura;
  }

  private mensagem(error: HttpErrorResponse, padrao: string): string {
    return typeof error.error === 'string' && error.error ? error.error : padrao;
  }
}
